import type { FirebaseError } from 'firebase/app'

import { z } from 'zod'
import { toast } from 'sonner'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Loader2, Eye, EyeOff } from 'lucide-react'
import { zodResolver } from '@hookform/resolvers/zod'
import { verifyCurrentPassword } from '@/lib/firebase'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'

import NewPasswordDialog from './new-password-dialog'

// --- Types ---
interface CurrentPasswordFormValues {
  currentPassword: string
}

interface ChangePasswordViewProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  // If true, user cannot close the dialog (overlay click, ESC, or cancel)
  disableClose?: boolean
}

const currentPasswordSchema = z.object({
  currentPassword: z.string().min(1, 'กรุณากรอกรหัสผ่านปัจจุบัน'),
})

export default function ChangePasswordDialog({
  open,
  onOpenChange,
  disableClose = false,
}: ChangePasswordViewProps) {
  const [showPassword, setShowPassword] = useState(false)
  const [loading, setLoading] = useState(false)
  const [showNewPasswordDialog, setShowNewPasswordDialog] = useState(false)
  const [currentPassword, setCurrentPassword] = useState('')

  const form = useForm<CurrentPasswordFormValues>({
    resolver: zodResolver(currentPasswordSchema),
    defaultValues: {
      currentPassword: '',
    },
  })

  const handleVerifyCurrentPassword = async (
    formValue: CurrentPasswordFormValues,
  ) => {
    setLoading(true)
    try {
      // Verify current password using the function from firebase.ts
      await verifyCurrentPassword(formValue.currentPassword)

      setCurrentPassword(formValue.currentPassword)
      setShowNewPasswordDialog(true)
      toast.success('ยืนยันรหัสผ่านปัจจุบันสำเร็จ')
    } catch (error: unknown) {
      console.error('Verify current password error:', error)

      // Handle specific Firebase errors
      if (error instanceof Error && 'code' in error) {
        const firebaseError = error as FirebaseError
        if (
          firebaseError.code === 'auth/wrong-password' ||
          firebaseError.code === 'auth/invalid-credential'
        ) {
          toast.error('รหัสผ่านปัจจุบันไม่ถูกต้อง')
        } else if (firebaseError.code === 'auth/user-not-found') {
          toast.error('ไม่พบผู้ใช้นี้')
        } else if (firebaseError.code === 'auth/too-many-requests') {
          toast.error('มีการพยายามเข้าสู่ระบบมากเกินไป กรุณาลองใหม่ภายหลัง')
        } else {
          toast.error('เกิดข้อผิดพลาดในการยืนยันรหัสผ่าน')
        }
      } else {
        toast.error('เกิดข้อผิดพลาดในการยืนยันรหัสผ่าน')
      }
    } finally {
      setLoading(false)
    }
  }

  const handleCloseDialog = () => {
    if (disableClose) return
    setShowNewPasswordDialog(false)
    form.reset()
    setShowPassword(false)
    onOpenChange(false)
  }

  const handleNewPasswordDialogClose = () => {
    setShowNewPasswordDialog(false)
    form.reset()
    setShowPassword(false)
    // Always close the main dialog when NewPasswordDialog closes
    // This allows the dialog to close after successful password change
    onOpenChange(false)
  }

  return (
    <>
      <Dialog
        open={open}
        onOpenChange={(nextOpen) => {
          if (disableClose && !nextOpen) return
          onOpenChange(nextOpen)
        }}
      >
        <DialogContent
          aria-describedby=""
          className="max-w-md"
          onEscapeKeyDown={(e) => {
            if (disableClose) e.preventDefault()
          }}
          onInteractOutside={(e) => {
            if (disableClose) e.preventDefault()
          }}
        >
          <DialogHeader>
            <DialogTitle className="text-xl font-semibold text-gray-900">
              เปลี่ยนรหัสผ่าน
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <form
              onSubmit={form.handleSubmit(handleVerifyCurrentPassword)}
              className="space-y-4"
            >
              <p className="text-sm text-gray-600">
                กรุณากรอกรหัสผ่านปัจจุบันเพื่อยืนยันตัวตน
              </p>

              <div className="space-y-2">
                <div className="relative">
                  <Input
                    placeholder="รหัสผ่านปัจจุบัน"
                    type={showPassword ? 'text' : 'password'}
                    {...form.register('currentPassword')}
                    disabled={loading}
                    className="pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    disabled={loading}
                  >
                    {showPassword ? (
                      <EyeOff className="h-4 w-4" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                  </button>
                </div>
                {form.formState.errors.currentPassword && (
                  <p className="text-sm text-red-500">
                    {form.formState.errors.currentPassword.message}
                  </p>
                )}
              </div>

              <div className="flex gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleCloseDialog}
                  className="flex-1"
                  disabled={loading || disableClose}
                >
                  ยกเลิก
                </Button>
                <Button type="submit" className="flex-1" disabled={loading}>
                  {loading ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    'ยืนยันรหัสผ่านปัจจุบัน'
                  )}
                </Button>
              </div>
            </form>
          </div>
        </DialogContent>
      </Dialog>

      {/* New Password Dialog */}
      <NewPasswordDialog
        open={showNewPasswordDialog}
        onOpenChange={handleNewPasswordDialogClose}
        currentPassword={currentPassword}
      />
    </>
  )
}
